// Packages
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { Modal } from "react-bootstrap";
import { useIntl, FormattedMessage } from 'react-intl';
import "bootstrap/js/dist/dropdown.js";
// Icons, Images etc.
import NoImageAvailableIcon from "../../images/NoImageAvailableIcon.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCaretDown,
  faTag,
} from "@fortawesome/free-solid-svg-icons";
// Redux Operations
import { invoiceOperations } from "../../state/features/invoice";
// Helpers, Utils etc.
import { formatDecimal } from "../../helpers/itemCalculations";
import {
  translatedName,
} from "../../helpers/translations";
import { QUALITY_10, transformImageQuality } from "../../helpers/imageTransformation";
import { populateModifiers } from "../../helpers/utils";

interface IPastOrderDetailsPopupProps {
  pastOrder: any;
  onClose: any;
  currency: any;
  reorderhandler: any;
  onSendInvoiceEmailClick: any;
}

function PastOrderDetailsPopup(props: IPastOrderDetailsPopupProps) {
  const [show, setShow] = useState(true);

  const intl = useIntl();

  const handleClose = () => {
    setShow(false);
    setTimeout(() => {
      props.onClose();
    }, 500);
  };

  const dateFromString = (dateString: string) => {
    let date = new Date(dateString);

    return date.toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  const orderDetails = props.pastOrder?.order_details;

  return (
    <Modal
      show={show}
      dialogClassName="custom curved"
      size="lg"
      onHide={handleClose}
      centered
    >
      <div className="past-order-details">
        <h1 className="order-number">{props.pastOrder.order_number}</h1>
        <div className="order-info">
          <p className="order-type">
            {orderDetails?.order_type == "pickup" ? (
              <FormattedMessage
                id="my_account.take_away"
                defaultMessage="Take-away"
              />
            ) : (
              <FormattedMessage
                id="global.delivery"
                defaultMessage="Delivery"
              />
            )}
          </p>|
          <p className="order-date">
            {dateFromString(props.pastOrder.created_at)}
          </p>
        </div>
        <table className="order-details-table">
          <thead>
            <tr>
              <th className="name">
                <FormattedMessage
                  id="global.item"
                  defaultMessage="Item"
                />
              </th>
              <th>
                <FormattedMessage
                  id="global.quantity"
                  defaultMessage="Quantity"
                />
              </th>
              <th>
                <FormattedMessage
                  id="global.price"
                  defaultMessage="Price"
                />
              </th>
              <th>
                <FormattedMessage
                  id="global.total"
                  defaultMessage="Total"
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {orderDetails?.cartItems.map(
              (item: any, index: number) => {
                const selectedModifiers = populateModifiers(item);
                const promotionDetails = orderDetails?.items?.[index]?.discount;
                const promotionName = item.applied_promotion_id && promotionDetails?.name;
                const showDetails = selectedModifiers.length > 0 || promotionName;

                return (
                  <tr key={index}>
                    <td
                      className="name"
                      style={
                        showDetails
                          ? { verticalAlign: "top" }
                          : { verticalAlign: "center" }
                      }
                    >
                      <div className="d-flex align-items-center">
                        <div
                          className="item-image"
                          style={{
                            backgroundImage: `url(${
                              item.image_url
                                ? transformImageQuality(item.image_url, QUALITY_10)
                                : NoImageAvailableIcon
                            })`,
                          }}
                        />
                        <div>
                          <p>
                            {translatedName(item, intl.locale)}
                          </p>
                          {showDetails ? (
                            <div className="modifiers">
                              {selectedModifiers.map((modifier: any) => {
                                return (
                                  <p className="modifier-item" key={modifier.id}>
                                    {translatedName(modifier, intl.locale)}
                                    {modifier.unit_price > 0 ? (
                                      <span className="price">
                                        +{props.currency}{" "}
                                        {formatDecimal(modifier.unit_price)}
                                      </span>
                                    ) : " "}
                                    {"x " + modifier.count}
                                  </p>
                                );
                              })}
                            </div>
                          ) : null}
                        {promotionName ? (
                          <p className="promotion-name">
                            <FontAwesomeIcon icon={faTag} className="mr-1" />
                            {promotionName}
                          </p>
                        ) : null}
                        </div>
                      </div>
                    </td>
                    <td
                      style={
                        showDetails
                          ? { verticalAlign: "top" }
                          : { verticalAlign: "center" }
                      }
                    >
                      {item.count}
                    </td>
                    <td
                      style={
                        showDetails
                          ? { verticalAlign: "top" }
                          : { verticalAlign: "center" }
                      }
                    >
                      {item.unit_price}
                    </td>
                    <td
                      style={
                        showDetails
                          ? { verticalAlign: "top" }
                          : { verticalAlign: "center" }
                      }
                    >
                      {item.sub_total}
                    </td>
                  </tr>
                );
              }
            )}

          </tbody>
          <tfoot>
            <tr className="amount subtotal">
              <td className="label text-right" colSpan={3}>
                <FormattedMessage
                  id="global.subtotal"
                  defaultMessage="Subtotal"
                />
              </td>
              <td className="value text-right pr-3">{formatDecimal(orderDetails?.sub_total)}</td>
            </tr>

            {orderDetails?.tips ? (
              <tr className="amount">
                <td className="label text-right" colSpan={3}>
                  <FormattedMessage
                    id="global.tip"
                    defaultMessage="Tip"
                  />
                </td>
                <td className="value text-right pr-3">{formatDecimal(orderDetails?.tips)}</td>
              </tr>
            ) : (
              false
            )}

            {orderDetails?.surcharges_breakdown &&
              orderDetails?.surcharges_breakdown.length ? (
                orderDetails?.surcharges_breakdown.map((surcharge: any) => {
                  return (
                    <tr className="amount charges" key={surcharge.name}>
                      <td className="label text-right" colSpan={3}>{surcharge.name}</td>
                      <td className="value text-right pr-3">{formatDecimal(surcharge.amount)}</td>
                    </tr>
                  );
                })
              ) : false}

            {orderDetails?.discounts.length ? (
              <tr className="amount discounts">
                <td className="label text-right" colSpan={3}>{orderDetails?.discounts[0].name}</td>
                <td className="value text-right pr-3">-{formatDecimal(orderDetails?.discounts[0].amount)}</td>
              </tr>
            ) : (
              false
            )}

            {orderDetails?.redeemed_loyalty ? (
              <tr className="amount discounts">
                <td className="label text-right" colSpan={3}>
                  <FormattedMessage
                    id="global.loyalty"
                    defaultMessage="Loyalty"
                  />
                </td>
                <td className="value text-right pr-3">-{formatDecimal(orderDetails?.redeemed_loyalty)}</td>
              </tr>
            ) : (
              false
            )}
            {!orderDetails?.is_tax_inclusive &&
              orderDetails?.tax_total > 0 ? (
              <>
                {orderDetails?.tax_group_breakdown &&
                orderDetails?.tax_group_breakdown.length > 0 ? (
                  orderDetails?.tax_group_breakdown.map((tax: any) => {
                    return (
                      <tr className="amount tax" key={tax.tax_rate_id}>
                        <td className="label text-right" colSpan={3}>
                          {`${tax.tax_name} (${tax.tax_rate}%)`}
                        </td>
                        <td className="value text-right pr-3">
                          {formatDecimal(tax.tax_amount)}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr className="amount tax">
                    <td className="label text-right" colSpan={3}>
                      <FormattedMessage
                        id="global.tax"
                        defaultMessage="Tax"
                      />
                    </td>
                    <td className="value text-right pr-3">
                      {formatDecimal(orderDetails?.tax_total)}
                    </td>
                  </tr>
                )}
              </>
            ) : (
              false
            )}
            <tr className="amount total">
              <td className="label text-right" colSpan={3}>
                <FormattedMessage
                  id="global.total"
                  defaultMessage="Total"
                />
              </td>
              <td className="value text-right pr-3">{orderDetails?.total_amount}</td>
            </tr>
          </tfoot>
        </table>
        <div className="d-flex justify-content-end">
          <button
            onClick={handleClose}
            className="button px-4 btn  btn-outline-primary mr-3"
          >
            <FormattedMessage
              id="global.close"
              defaultMessage="Close"
            />
          </button>

          <div className="dropdown">
            <button
              className="button px-4 btn btn-outline-primary mr-3"
              type="button"
              id="invoiceDropDownButton"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              <FormattedMessage
                id="my_account.invoice"
                defaultMessage="Invoice"
              />
              <FontAwesomeIcon className="ml-1" size="1x" icon={faCaretDown} />
            </button>
            <div className="dropdown-menu invoice-dropdown" aria-labelledby="dropdownMenuButton">
              <Link to={`/invoices/${props.pastOrder.order_number}.pdf`} target="_blank" className="dropdown-item invoice-dropdown-item">
                <FormattedMessage
                  id="my_account.invoice.print"
                  defaultMessage="Print Invoice"
                />
              </Link>
              <Link to={`/invoices/${props.pastOrder.order_number}.pdf?download=true`} target="_blank" className="dropdown-item invoice-dropdown-item">
                <FormattedMessage
                  id="my_account.invoice.download"
                  defaultMessage="Download Invoice"
                />
              </Link>
              <button
                className="dropdown-item invoice-dropdown-item"
                onClick={props.onSendInvoiceEmailClick}
              >
                <FormattedMessage
                  id="my_account.invoice.send_via_email"
                  defaultMessage="Send Invoice via Email"
                />
              </button>
            </div>
          </div>

          <button
            onClick={() => props.reorderhandler(props.pastOrder)}
            className="button px-4 btn btn-primary"
          >
            <FormattedMessage
              id="my_account.re_order"
              defaultMessage="Re-Order"
            />
          </button>
        </div>
      </div>
    </Modal>
  );
}

const mapStateToProps = (state: any) => {
  let currency = state.company.currency;

  return {
    currency,
  };
};

export default connect(mapStateToProps)(PastOrderDetailsPopup);
